export enum eWhatsappApiWindow {
  GENERAL = 'GENERAL',
  VIEW_TEMPLATES = 'VIEW_TEMPLATES',
  CREATE_TEMPLATE = 'CREATE_TEMPLATE',
  EDIT_TEMPLATE = 'EDIT_TEMPLATE',
  CAMPAIGNS = 'CAMPAIGNS',
  CREDITS = 'CREDITS',
  // RET-1689 shipped this as additive — Saldo alongside CREDITS. RET-2105
  // revokes that: with billing_wapi-wallet on, Saldo takes CREDITS' seat
  // instead of sitting next to it. See HeaderSelection.component.tsx for the
  // cohort filter that keeps the two from ever showing together.
  BALANCE = 'BALANCE',
  FLOWS = 'FLOWS',
}

export enum eButtonType {
  QUICK_REPLY = 'QUICK_REPLY',
  URL = 'URL',
  PHONE_NUMBER = 'PHONE_NUMBER',
  COPY_CODE = 'COPY_CODE',
  OTP = 'OTP',
  MPM = 'MPM',
  CATALOG = 'CATALOG',
  FLOW = 'FLOW',
  VOICE_CALL = 'VOICE_CALL',
  APP = 'APP',
}

export enum eComponentType {
  HEADER = 'HEADER',
  BODY = 'BODY',
  FOOTER = 'FOOTER',
  BUTTONS = 'BUTTONS',
  // INT-1268 — carousel templates synced from Meta. Render-only for now:
  // the create/edit wizard does not build CAROUSEL components.
  CAROUSEL = 'CAROUSEL',
}

export enum eFormatType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
  MEDIA = 'MEDIA', // backward compat: backend returns this for existing templates
}

export enum eTemplateCategory {
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
  OTP = 'OTP',
  VERIFICATION = 'VERIFICATION',
}

export enum eParameterFormat {
  NAMED = 'NAMED',
  POSITIONAL = 'POSITIONAL',
}
export enum eAccountStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  CONNECTED = 'CONNECTED',
}

export enum eConnectionStatus {
  BANNED = 'BANNED',
  CONNECTED = 'CONNECTED',
  DELETED = 'DELETED',
  DISCONNECTED = 'DISCONNECTED',
  FLAGGED = 'FLAGGED',
  MIGRATED = 'MIGRATED',
  PENDING = 'PENDING',
  RATE_LIMITED = 'RATE_LIMITED',
  RESTRICTED = 'RESTRICTED',
  UNKNOWN = 'UNKNOWN',
}

export enum eQualityRating {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Mirrors enums.BusinessVerificationStatus on the WhatsApp Business backend.
 * The backend collapses anything Meta returns outside this list to UNKNOWN,
 * so the UI never has to handle a value it has not seen.
 */
export enum eBusinessVerificationStatus {
  VERIFIED = 'VERIFIED',
  NOT_VERIFIED = 'NOT_VERIFIED',
  PENDING = 'PENDING',
  PENDING_NEED_MORE_INFO = 'PENDING_NEED_MORE_INFO',
  PENDING_SUBMISSION = 'PENDING_SUBMISSION',
  EXPIRED = 'EXPIRED',
  REJECTED = 'REJECTED',
  REVOKED = 'REVOKED',
  FAILED = 'FAILED',
  INELIGIBLE = 'INELIGIBLE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Visual bucket the badge renders in. Keeps the UI on three states
 * (positive / pending / negative) plus an unknown fallback regardless of how
 * many verification states Meta adds upstream.
 */
export enum eBusinessVerificationBucket {
  VERIFIED = 'VERIFIED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Send eligibility from Meta's health_status, mirroring
 * enums.CanSendMessageStatus on the whatsapp-business-service backend — minus
 * its UNKNOWN sentinel. An unrecognized or absent value is resolved to `null`
 * (see resolveCanSendMessage), so the connection badge degrades to its binary
 * connected/disconnected fallback rather than rendering an unhandled state.
 */
export enum eCanSendMessage {
  AVAILABLE = 'AVAILABLE',
  LIMITED = 'LIMITED',
  BLOCKED = 'BLOCKED',
}

/**
 * Categorization of Meta's free-form `send_restriction_reason` into the
 * buckets the ConnectionStatusModal can explain (INT-951). The classifier
 * lives in GeneralSection.utils.ts and returns UNKNOWN for any string it
 * can't match — that path routes to a generic Spanish wrapper that quotes
 * Meta's raw English verbatim, so a misclassification or new Meta wording
 * degrades to "show the customer the truth" rather than "lie under a known
 * header."
 */
export enum eSendRestrictionCategory {
  SPAM_REPORTS = 'SPAM_REPORTS',
  QUALITY_DROP = 'QUALITY_DROP',
  DISPLAY_NAME_PENDING = 'DISPLAY_NAME_PENDING',
  RATE_RESTRICTION = 'RATE_RESTRICTION',
  UNKNOWN = 'UNKNOWN',
}

export enum eFailureGroup {
  LEAD_EXCLUDED = 'LEAD_EXCLUDED',
  SYSTEM_ERROR = 'SYSTEM_ERROR',
  SPAM_RATE_LIMIT = 'SPAM_RATE_LIMIT',
  REGISTRATION_ERROR = 'REGISTRATION_ERROR',
  ACCOUNT_BLOCKED = 'ACCOUNT_BLOCKED',
}

/** Values are lowercase intentionally — used for CSS class interpolation (`--${status}`) and i18n key lookup (`page.templateHealth.${status}`). */
export enum eTemplateHealthStatus {
  HEALTHY = 'healthy',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

